import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormLabel,
} from '@mui/material';
import { elegantMenuProps } from '../../utils/menuStyles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowUpward,
  ArrowDownward,
  Search as SearchIcon,
  SubdirectoryArrowRight,
} from '@mui/icons-material';
import {
  QuestionPaperSection,
  QuestionPaperQuestion,
} from '../../services/generateQuestionPaperService';
import {
  QuestionBank,
  Chapter,
  Question,
} from '../../services/questionPaperService';
import QuestionBankBrowser from './QuestionBankBrowser';
import SectionQuestionCard from './SectionQuestionCard';
import BankWideSearchDialog from '../questionBank/BankWideSearchDialog';
import ChoiceGroupBlock from './ChoiceGroupBlock';
import {
  buildSectionRenderItems,
  itemEffectiveMarks,
  sectionEffectiveMarks,
  sectionMarksAreUniform,
  RenderItem,
  RenderQuestionItem,
  RenderChoiceGroupItem,
  RenderParentGroupItem,
} from '../../utils/paperLayout';
import {
  NumberingStyle,
  MCQOptionStyle,
  formatNumber,
  NUMBERING_STYLES,
  NUMBERING_STYLE_LABELS,
} from '../../utils/paperNumbering';

interface SectionEditorProps {
  section: QuestionPaperSection;
  sectionIndex: number;
  isExpanded: boolean;
  isViewMode: boolean;
  isFirst: boolean;
  isLast: boolean;
  canEdit: boolean;
  questionBanks: QuestionBank[];
  chapters: Chapter[];
  /** Paper-wide question numbering style — surfaced on each question chip. */
  questionNumberStyle?: NumberingStyle;
  /** Paper-wide MCQ option style — forwarded to each SectionQuestionCard so
   *  the in-editor preview matches the review + PDF output. */
  mcqOptionStyle?: MCQOptionStyle;
  /** True when this section is a subsection (has a parent). Drives indent
   *  + labelling. */
  isSubsection?: boolean;
  /** True when this section has any subsections nested under it — hides the
   *  "Add subsection" button on subsections (only one level deep for v1). */
  hasSubsections?: boolean;
  onToggleExpand: (index: number) => void;
  onUpdateSection: (index: number, updates: Partial<QuestionPaperSection>) => void;
  onDeleteSection: (index: number) => void;
  onMoveSectionUp: (index: number) => void;
  onMoveSectionDown: (index: number) => void;
  onAddSubsection?: (parentIndex: number) => void;
  onAddQuestionFromBank: (sectionIndex: number, question: Question) => void;
  onOpenCustomQuestion: (sectionIndex: number) => void;
  onUpdateQuestionMarks: (sectionIndex: number, questionIndex: number, marks: number) => void;
  onDeleteQuestion: (sectionIndex: number, questionIndex: number) => void;
  onEditMCQ: (qIndex: number, sectionIndex: number, question: QuestionPaperQuestion) => void;
  onUpdateQuestionText: (sectionIndex: number, questionIndex: number, text: string) => void;
  /** Paper-wide sub-part numbering style — used for children under a parent. */
  subPartStyle?: NumberingStyle;
  /** Choice-group handlers — turn a question into an OR pair, or remove one. */
  onAddOrAlternative?: (sectionIndex: number, questionIndex: number) => void;
  /** Called with bank picks that should become OR alternatives to the source question. */
  onAddOrAlternativesFromBank?: (sectionIndex: number, questionIndex: number, picked: Question[]) => void;
  onRemoveFromChoiceGroup?: (sectionIndex: number, questionIndex: number) => void;
  /** Sub-question handlers — add a blank child, add bank children, or detach. */
  onAddSubQuestion?: (sectionIndex: number, questionIndex: number) => void;
  onAddSubQuestionsFromBank?: (sectionIndex: number, questionIndex: number, picked: Question[]) => void;
  onRemoveFromSubGroup?: (sectionIndex: number, questionIndex: number) => void;
  /** Fires when the user clicks the "Sync from bank" icon on a card. Only
   * meaningful for rows sourced from the bank (`questionId` set). Parent
   * tracks in-flight ids in `syncingIds` so we can spin the icon. */
  onResyncQuestionFromBank?: (sectionIndex: number, questionIndex: number) => void;
  syncingIds?: Set<string>;
}

const SectionEditor = memo<SectionEditorProps>(({
  section,
  sectionIndex,
  isExpanded,
  isViewMode,
  isFirst,
  isLast,
  canEdit,
  questionBanks,
  chapters,
  questionNumberStyle,
  mcqOptionStyle,
  isSubsection = false,
  hasSubsections = false,
  onToggleExpand,
  onUpdateSection,
  onDeleteSection,
  onMoveSectionUp,
  onAddSubsection,
  onMoveSectionDown,
  onAddQuestionFromBank,
  onOpenCustomQuestion,
  onUpdateQuestionMarks,
  onDeleteQuestion,
  onEditMCQ,
  onUpdateQuestionText,
  subPartStyle,
  onAddOrAlternative,
  onAddOrAlternativesFromBank,
  onRemoveFromChoiceGroup,
  onAddSubQuestion,
  onAddSubQuestionsFromBank,
  onRemoveFromSubGroup,
  onResyncQuestionFromBank,
  syncingIds,
}) => {
  const handleToggle = useCallback(() => {
    onToggleExpand(sectionIndex);
  }, [onToggleExpand, sectionIndex]);

  const [bankSearchOpen, setBankSearchOpen] = useState(false);
  // When non-null, the bank picker opens in "OR alternative" mode — picks are
  // stamped as siblings of `orBankQuestionIndex` (sharing its choiceGroupId).
  const [orBankQuestionIndex, setOrBankQuestionIndex] = useState<number | null>(null);
  // When non-null, the bank picker opens in "sub-question" mode — picks
  // become children of `subBankQuestionIndex` (a parent question).
  const [subBankQuestionIndex, setSubBankQuestionIndex] = useState<number | null>(null);

  // For this paper, the class+subject pin down exactly ONE question bank (the
  // model has unique (classId, subjectId, schoolId)). Pick the first; if no
  // bank exists yet the button stays disabled.
  const primaryBank = questionBanks[0];

  // Ids already in this section so the dialog can mute / disable them.
  const alreadyAddedIds = useMemo(() => {
    const set = new Set<string>();
    for (const q of section.questions || []) {
      if (q.questionId) set.add(String(q.questionId));
    }
    return set;
  }, [section.questions]);

  const handleAddSelectedFromBank = useCallback((picked: Question[]) => {
    // Fire the parent's single-question handler for each pick. Keeps the
    // existing data-flow contract intact — no new pathways to maintain.
    for (const q of picked) onAddQuestionFromBank(sectionIndex, q);
  }, [onAddQuestionFromBank, sectionIndex]);

  const handleTriggerOrFromBank = useCallback((_sIdx: number, questionIndex: number) => {
    setOrBankQuestionIndex(questionIndex);
  }, []);

  const handleOrBankPicks = useCallback((picked: Question[]) => {
    if (orBankQuestionIndex !== null && onAddOrAlternativesFromBank) {
      onAddOrAlternativesFromBank(sectionIndex, orBankQuestionIndex, picked);
    }
    setOrBankQuestionIndex(null);
  }, [orBankQuestionIndex, onAddOrAlternativesFromBank, sectionIndex]);

  const handleTriggerSubFromBank = useCallback((_sIdx: number, questionIndex: number) => {
    setSubBankQuestionIndex(questionIndex);
  }, []);

  const handleSubBankPicks = useCallback((picked: Question[]) => {
    if (subBankQuestionIndex !== null && onAddSubQuestionsFromBank) {
      onAddSubQuestionsFromBank(sectionIndex, subBankQuestionIndex, picked);
    }
    setSubBankQuestionIndex(null);
  }, [subBankQuestionIndex, onAddSubQuestionsFromBank, sectionIndex]);

  // Build the layout-aware render items (choice groups inside runs of
  // standalone questions, parent-group wrapping). Mirrors what ReviewStep /
  // the PDF template do, so authoring == preview == export.
  const renderItems = useMemo<RenderItem[]>(
    () => buildSectionRenderItems({
      section: { _id: section._id || `s-${sectionIndex}` },
      questions: (section.questions || []) as any[],
    }).items,
    [section._id, sectionIndex, section.questions],
  );

  // "Attempt any N of M" only produces a deterministic total when every item
  // in the section contributes the same effective marks. Surface a hint
  // (uniform value) or a hard error (mixed values) next to the rule inputs.
  const hasSelectionRule = !!section.selectionRule?.attemptCount;
  const uniformItemMarks = useMemo(() => {
    if (!hasSelectionRule || renderItems.length === 0) return null;
    if (!sectionMarksAreUniform(renderItems)) return null;
    return itemEffectiveMarks(renderItems[0]);
  }, [hasSelectionRule, renderItems]);
  const selectionMarksError = hasSelectionRule
    && renderItems.length > 0
    && !sectionMarksAreUniform(renderItems);

  // Reverse index from a question reference to its flat index in
  // `section.questions`. `buildSectionRenderItems` preserves object identity,
  // so this lookup is stable across sorts and groupings.
  const questionFlatIndex = useMemo(() => {
    const m = new Map<QuestionPaperQuestion, number>();
    (section.questions || []).forEach((q, i) => m.set(q, i));
    return m;
  }, [section.questions]);

  const renderQuestionCard = useCallback((
    item: RenderQuestionItem,
    opts?: { hideNumber?: boolean; parentMode?: boolean; childLabel?: string; childMarks?: number },
  ) => {
    const q = item.question as unknown as QuestionPaperQuestion;
    const flat = questionFlatIndex.get(q);
    if (flat === undefined) return null;
    const inChoiceGroup = !!q.choiceGroupId;
    const isParent = q.subGroupRole === 'parent';
    const isChild = q.subGroupRole === 'child';
    return (
      <SectionQuestionCard
        key={q._id || `q-${flat}`}
        question={q}
        qIndex={flat}
        sectionIndex={sectionIndex}
        isViewMode={isViewMode}
        questionNumber={item.questionNumber}
        questionNumberStyle={questionNumberStyle}
        mcqOptionStyle={mcqOptionStyle}
        hideQuestionNumber={opts?.hideNumber}
        childLabel={opts?.childLabel}
        childMarks={opts?.childMarks}
        parentMode={opts?.parentMode}
        inChoiceGroup={inChoiceGroup}
        isParent={isParent}
        isChild={isChild}
        onUpdateMarks={onUpdateQuestionMarks}
        onDelete={onDeleteQuestion}
        onEditMCQ={onEditMCQ}
        onUpdateQuestionText={onUpdateQuestionText}
        // OR / choice-group actions — hidden when the question is a parent or child.
        onAddOrAlternative={!isParent && !isChild ? onAddOrAlternative : undefined}
        onAddOrAlternativeFromBank={!isParent && !isChild && onAddOrAlternativesFromBank ? handleTriggerOrFromBank : undefined}
        onRemoveFromChoiceGroup={onRemoveFromChoiceGroup}
        // Sub-question actions — hidden when the question is already in a choice group or is itself a child.
        onAddSubQuestion={!inChoiceGroup && !isChild ? onAddSubQuestion : undefined}
        onAddSubQuestionFromBank={!inChoiceGroup && !isChild && onAddSubQuestionsFromBank ? handleTriggerSubFromBank : undefined}
        onRemoveFromSubGroup={(isParent || isChild) ? onRemoveFromSubGroup : undefined}
        onResyncFromBank={onResyncQuestionFromBank}
        isSyncing={!!(q._id && syncingIds?.has(q._id))}
      />
    );
  }, [
    questionFlatIndex, sectionIndex, isViewMode, questionNumberStyle, mcqOptionStyle,
    onUpdateQuestionMarks, onDeleteQuestion, onEditMCQ, onUpdateQuestionText,
    onAddOrAlternative, onAddOrAlternativesFromBank, handleTriggerOrFromBank,
    onRemoveFromChoiceGroup,
    onAddSubQuestion, onAddSubQuestionsFromBank, handleTriggerSubFromBank, onRemoveFromSubGroup,
    onResyncQuestionFromBank, syncingIds,
  ]);

  const renderChoiceGroup = useCallback((item: RenderChoiceGroupItem) => (
    <ChoiceGroupBlock
      key={item.choiceGroupId}
      label={item.label}
      memberCount={item.members.length}
    >
      {item.members.map((m, idx) => renderQuestionCard(m, { hideNumber: idx > 0 }))}
    </ChoiceGroupBlock>
  ), [renderQuestionCard]);

  const renderParentGroup = useCallback((item: RenderParentGroupItem) => {
    const compositeMarks = item.children.reduce((sum, c) => sum + (c.question.marks || 0), 0);
    return (
      <Box key={item.subGroupId} sx={{ mb: 1 }}>
        {renderQuestionCard(item.parent, { parentMode: true, childMarks: compositeMarks })}
        <Box sx={{ ml: 3, mt: -0.5 }}>
          {item.children.map((c) => {
            const label = formatNumber(c.subPartIndex, subPartStyle || 'lowerRoman');
            const wrapped = /[().]/.test(label) ? label : `(${label})`;
            return renderQuestionCard(
              { kind: 'question', question: c.question, questionNumber: c.subPartIndex },
              { hideNumber: true, childLabel: wrapped, childMarks: c.question.marks || 0 },
            );
          })}
        </Box>
      </Box>
    );
  }, [renderQuestionCard, subPartStyle]);

  const renderLayoutItem = useCallback((item: RenderItem): React.ReactNode => {
    if (item.kind === 'question')     return renderQuestionCard(item);
    if (item.kind === 'choice-group') return renderChoiceGroup(item);
    return renderParentGroup(item);
  }, [renderQuestionCard, renderChoiceGroup, renderParentGroup]);

  const totalQuestions = section.questions?.length || 0;

  // Section-rule handlers — clearing the "attempt any" field wipes the whole
  // rule (a rule with attempt=0 is nonsensical). The out-of field is bounded
  // by the number of items in the section so authors don't type garbage.
  const handleSectionMarksChange = (value: string) => {
    if (value === '') { onUpdateSection(sectionIndex, { marks: undefined }); return; }
    const parsed = parseInt(value);
    onUpdateSection(sectionIndex, { marks: Number.isFinite(parsed) ? Math.max(0, parsed) : undefined });
  };

  const handleAttemptChange = (value: string) => {
    if (value === '' || value === '0') {
      onUpdateSection(sectionIndex, { selectionRule: undefined });
      return;
    }
    const attempt = Math.max(1, parseInt(value) || 1);
    const currentTotal = section.selectionRule?.totalCount;
    const total = currentTotal && currentTotal >= attempt ? currentTotal : Math.max(attempt, totalQuestions);
    onUpdateSection(sectionIndex, { selectionRule: { attemptCount: attempt, totalCount: total } });
  };

  const handleTotalChange = (value: string) => {
    if (!section.selectionRule) return;
    const total = Math.max(section.selectionRule.attemptCount, parseInt(value) || 0);
    onUpdateSection(sectionIndex, {
      selectionRule: { attemptCount: section.selectionRule.attemptCount, totalCount: total },
    });
  };

  // Effective numbering mode — favours the explicit `numberingMode`, falls
  // back to the legacy `numberingContinues` boolean for older rows.
  const effectiveMode: 'restart' | 'continue' | 'startAt' =
    section.numberingMode || (section.numberingContinues ? 'continue' : 'restart');

  const handleNumberingModeChange = (mode: 'restart' | 'continue' | 'startAt') => {
    const updates: Partial<QuestionPaperSection> = { numberingMode: mode };
    // Keep the legacy boolean in sync for backwards read-compat.
    updates.numberingContinues = mode === 'continue';
    if (mode !== 'startAt') updates.numberingStartAt = undefined;
    if (mode === 'startAt' && !section.numberingStartAt) updates.numberingStartAt = 1;
    onUpdateSection(sectionIndex, updates);
  };

  return (
    <>
    <Accordion
      expanded={isExpanded}
      onChange={handleToggle}
      sx={{
        mb: isSubsection ? 1 : 2,
        // Visual nesting for subsections — soft left indent + thinner border
        // + accent stripe to make the hierarchy obvious at a glance.
        ...(isSubsection && {
          ml: 4,
          borderLeft: '3px solid',
          borderLeftColor: 'primary.light',
          bgcolor: (theme: any) => theme.palette.action.hover,
        }),
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mr={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {isSubsection && (
              <Chip
                label="Subsection"
                size="small"
                variant="outlined"
                color="primary"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, letterSpacing: 0.3 }}
              />
            )}
            <Box>
              <Typography fontWeight={600}>{section.name}</Typography>
              {section.description && (
                <Typography variant="caption" color="text.secondary">{section.description}</Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Chip
              label={`${totalQuestions} questions`}
              size="small"
              color="primary"
            />
            <Chip
              label={`${
                // Mirrors the authoritative mark logic used by the paper
                // total + per-section PDF header: honour the `marks`
                // override first, else the `selectionRule`-adjusted sum.
                // Without this, the header chip double-counts raw marks
                // and disagrees with the printed section total.
                typeof section.marks === 'number' && section.marks > 0
                  ? section.marks
                  : sectionEffectiveMarks(renderItems, section.selectionRule || null)
              } marks`}
              size="small"
            />
            {canEdit && (
              <>
                {!isSubsection && !hasSubsections && onAddSubsection && (
                  <Tooltip title="Add a subsection nested under this section">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={isViewMode}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddSubsection(sectionIndex);
                        }}
                      >
                        <SubdirectoryArrowRight fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {!isSubsection && hasSubsections && onAddSubsection && (
                  <Tooltip title="Add another subsection">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={isViewMode}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddSubsection(sectionIndex);
                        }}
                      >
                        <SubdirectoryArrowRight fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Move Up">
                  <span>
                    <IconButton
                      size="small"
                      disabled={isFirst || isViewMode}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveSectionUp(sectionIndex);
                      }}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move Down">
                  <span>
                    <IconButton
                      size="small"
                      disabled={isLast || isViewMode}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveSectionDown(sectionIndex);
                      }}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={isSubsection ? 'Delete Subsection' : 'Delete Section'}>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={isViewMode}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSection(sectionIndex);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {/* Section Details */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Section Name"
                value={section.name}
                onChange={(e) => onUpdateSection(sectionIndex, { name: e.target.value })}
                disabled={isViewMode}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={section.description}
                onChange={(e) => onUpdateSection(sectionIndex, { description: e.target.value })}
                disabled={isViewMode}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Instructions"
                multiline
                rows={2}
                value={section.instructions}
                onChange={(e) => onUpdateSection(sectionIndex, { instructions: e.target.value })}
                placeholder="e.g., Please attempt 2 questions from this section."
                disabled={isViewMode}
              />
            </Grid>
          </Grid>

          {/* Section rules — optional advanced controls. Blank fields mean
              "sum all questions" (marks) and "attempt all" (selection rule). */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Section rules (optional)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Section marks (override)"
                  placeholder="Auto"
                  value={section.marks ?? ''}
                  onChange={(e) => handleSectionMarksChange(e.target.value)}
                  disabled={isViewMode}
                  inputProps={{ min: 0 }}
                  helperText="Leave blank to sum questions"
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Attempt any"
                  placeholder="—"
                  value={section.selectionRule?.attemptCount ?? ''}
                  onChange={(e) => handleAttemptChange(e.target.value)}
                  disabled={isViewMode}
                  inputProps={{ min: 0 }}
                  error={selectionMarksError}
                  helperText={
                    selectionMarksError
                      ? 'All questions must have equal marks'
                      : uniformItemMarks !== null && section.selectionRule
                        ? `${section.selectionRule.attemptCount} × ${uniformItemMarks} = ${section.selectionRule.attemptCount * uniformItemMarks} marks`
                        : 'e.g. 5 of 8'
                  }
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Out of"
                  placeholder="—"
                  value={section.selectionRule?.totalCount ?? ''}
                  onChange={(e) => handleTotalChange(e.target.value)}
                  disabled={isViewMode || !section.selectionRule?.attemptCount}
                  inputProps={{ min: section.selectionRule?.attemptCount || 0 }}
                  helperText={section.selectionRule ? 'Printed count' : 'Set "Attempt any" first'}
                />
              </Grid>
              {/* Numbering behaviour — three explicit modes. Renders as a
                  vertical radio group so the "Start at N" input can slide in
                  right under its own option without shifting the others. */}
              <Grid size={{ xs: 12 }}>
                <FormLabel
                  component="legend"
                  sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, mt: 0.5, display: 'block' }}
                >
                  Question numbering — where does this section's Q1 start?
                </FormLabel>
                <RadioGroup
                  value={effectiveMode}
                  onChange={(e) => handleNumberingModeChange(e.target.value as any)}
                >
                  <FormControlLabel
                    value="restart"
                    disabled={isViewMode}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        <strong>Restart at 1</strong>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          — this section's questions start fresh
                        </Typography>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="continue"
                    disabled={isViewMode || sectionIndex === 0}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        <strong>Continue from previous section</strong>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          {sectionIndex === 0 ? '— not available on the first section' : '— pick up where the previous section left off'}
                        </Typography>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="startAt"
                    disabled={isViewMode}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        <strong>Start at a specific number</strong>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          — you pick the exact starting value
                        </Typography>
                      </Typography>
                    }
                  />
                  {effectiveMode === 'startAt' && (
                    <Box sx={{ ml: 4, mt: 0.5, mb: 0.5 }}>
                      <TextField
                        size="small"
                        type="number"
                        label="Start at"
                        value={section.numberingStartAt ?? 1}
                        onChange={(e) => {
                          const v = Math.max(1, parseInt(e.target.value) || 1);
                          onUpdateSection(sectionIndex, { numberingStartAt: v });
                        }}
                        disabled={isViewMode}
                        inputProps={{ min: 1 }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                  )}
                </RadioGroup>
              </Grid>

              {/* Numbering style — optional per-section overrides. "Paper
                  default (…)" is the muted first option; a small primary dot
                  appears in the label once an override is set. */}
              <Grid size={{ xs: 12 }}>
                <FormLabel
                  component="legend"
                  sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, mt: 1, display: 'block' }}
                >
                  Numbering style — override the paper default for this section (optional)
                </FormLabel>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small" disabled={isViewMode}>
                      <InputLabel>
                        Question style
                        {section.questionStyleOverride && (
                          <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', ml: 0.75, verticalAlign: 'middle' }} />
                        )}
                      </InputLabel>
                      <Select
                        MenuProps={elegantMenuProps}
                        value={section.questionStyleOverride || ''}
                        label={`Question style${section.questionStyleOverride ? ' •' : ''}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          onUpdateSection(sectionIndex, { questionStyleOverride: (v || undefined) as NumberingStyle | undefined });
                        }}
                      >
                        <MenuItem value="">
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            Paper default{questionNumberStyle ? ` (${NUMBERING_STYLE_LABELS[questionNumberStyle]})` : ''}
                          </Typography>
                        </MenuItem>
                        {NUMBERING_STYLES.map((s) => (
                          <MenuItem key={s} value={s}>{NUMBERING_STYLE_LABELS[s]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small" disabled={isViewMode}>
                      <InputLabel>
                        Sub-part style
                        {section.subPartStyleOverride && (
                          <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', ml: 0.75, verticalAlign: 'middle' }} />
                        )}
                      </InputLabel>
                      <Select
                        MenuProps={elegantMenuProps}
                        value={section.subPartStyleOverride || ''}
                        label={`Sub-part style${section.subPartStyleOverride ? ' •' : ''}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          onUpdateSection(sectionIndex, { subPartStyleOverride: (v || undefined) as NumberingStyle | undefined });
                        }}
                      >
                        <MenuItem value="">
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            Paper default{subPartStyle ? ` (${NUMBERING_STYLE_LABELS[subPartStyle]})` : ''}
                          </Typography>
                        </MenuItem>
                        {NUMBERING_STYLES.map((s) => (
                          <MenuItem key={s} value={s}>{NUMBERING_STYLE_LABELS[s]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Add Questions Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={600}>Questions</Typography>
            {!isViewMode && (
              <Box display="flex" gap={1} flexWrap="wrap">
                <Tooltip title={primaryBank ? '' : 'No question bank for this class & subject yet'}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SearchIcon />}
                      onClick={() => setBankSearchOpen(true)}
                      disabled={!primaryBank}
                    >
                      Search bank
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => onOpenCustomQuestion(sectionIndex)}
                >
                  Add Custom Question
                </Button>
              </Box>
            )}
          </Box>

          {/* Question Bank Browser */}
          <QuestionBankBrowser
            questionBanks={questionBanks}
            chapters={chapters}
            sectionIndex={sectionIndex}
            onAddQuestion={onAddQuestionFromBank}
            alreadyAddedIds={alreadyAddedIds}
          />

          <Divider sx={{ my: 2 }} />

          {/* Selected Questions — driven by the shared paperLayout util so
              passages, choice groups, and standalone questions render in the
              same order the exam will print in. */}
          <Typography variant="subtitle2" gutterBottom>
            Questions in this section ({totalQuestions})
          </Typography>
          {renderItems.length === 0 ? (
            <Alert severity="info">No questions added yet</Alert>
          ) : (
            <Box>{renderItems.map(renderLayoutItem)}</Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>

    {/* Bank-wide search in selection mode — opened by the "Search bank"
        button above. Confirmed picks flow through the parent's existing
        onAddQuestionFromBank handler (one per question) so the section
        state-update path is unchanged. */}
    {primaryBank && (
      <BankWideSearchDialog
        open={bankSearchOpen}
        onClose={() => setBankSearchOpen(false)}
        questionBankId={primaryBank._id}
        chapters={chapters}
        alreadyAddedIds={alreadyAddedIds}
        onAddSelected={handleAddSelectedFromBank}
      />
    )}
    {/* Bank picker in "OR alternative" mode — every pick becomes a sibling of
        the source question inside the same choice group. */}
    {primaryBank && (
      <BankWideSearchDialog
        open={orBankQuestionIndex !== null}
        onClose={() => setOrBankQuestionIndex(null)}
        questionBankId={primaryBank._id}
        chapters={chapters}
        alreadyAddedIds={alreadyAddedIds}
        onAddSelected={handleOrBankPicks}
      />
    )}
    {/* Bank picker in "sub-question" mode — every pick becomes a child of
        the source question, sharing its subGroupId. */}
    {primaryBank && (
      <BankWideSearchDialog
        open={subBankQuestionIndex !== null}
        onClose={() => setSubBankQuestionIndex(null)}
        questionBankId={primaryBank._id}
        chapters={chapters}
        alreadyAddedIds={alreadyAddedIds}
        onAddSelected={handleSubBankPicks}
      />
    )}
    </>
  );
});

SectionEditor.displayName = 'SectionEditor';

export default SectionEditor;
